import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  StepList, RichFAQ, RelatedLinks, RichCTA,
} from '../RichBlocks';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * Elselskaber oversigt 2026 — bespoke "how to choose" guide. Geo-aware.
 * Neutral guide (no competitor pricing). Points users to Strømligning for
 * independent up-to-date comparisons. Registered base + geo-variants.
 */
export default function ElselskaberOversigt({ page }) {
  const geo = page?.geography;
  const place = geo?.name ? ` i ${geo.name}` : '';
  const zone = geo?.zone;
  const zoneLabel = zone === 'DK1' ? 'DK1 (Vestdanmark)' : zone === 'DK2' ? 'DK2 (Østdanmark)' : null;

  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'Hvordan finder jeg det billigste elselskab i 2026?',
      answer: 'Se på tre ting: tillægget til spotprisen (øre/kWh), abonnementsprisen (kr./md.) og hvilke services der er inkluderet. Den fysiske strøm er identisk uanset selskab. Brug en uafhængig sammenligning som Strømligning.dk til at se aktuelle priser for dit forbrug, og husk at medregne elbil-fordele som Saving Mode og laderabat, hvis du har elbil.',
    },
    {
      question: 'Er den dyreste strøm bedre kvalitet?',
      answer: 'Nej. Strømmen er fysisk den samme uanset pris og leverandør – den leveres via dit lokale netselskab. Et dyrere selskab giver ikke "bedre" strøm. Du betaler for tillæg, abonnement og eventuelle services, ikke for selve elektronernes kvalitet.',
    },
    {
      question: 'Hvad skal jeg være opmærksom på, når jeg vælger elselskab?',
      answer: 'Pas på introtilbud, der stiger efter få måneder, bindingsperioder, oprettelsesgebyrer og høje tillæg til spotprisen. Vælg gennemsigtige aftaler med spotpris uden tillæg, lavt abonnement og ingen binding. Har du elbil, vægter inkluderede elbil-services tungt.',
    },
    {
      question: 'Kan jeg frit vælge elselskab uanset hvor jeg bor?',
      answer: 'Ja. Alle danske husstande kan frit vælge elleverandør. Strømmen leveres altid af dit lokale netselskab, så du kan vælge enhver leverandør uanset adresse – også enkel energi.',
    },
    {
      question: 'Hvor ofte bør jeg tjekke mit elselskab?',
      answer: 'Mindst én gang om året. Mange bliver siddende på en dyr aftale, fordi et godt introtilbud er udløbet. Et hurtigt tjek på en uafhængig sammenligning kan ofte spare flere hundrede kroner om året.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'kriterier', label: '3 kriterier' },
    { id: 'faldgruber', label: 'Faldgruber' },
    { id: 'enkel', label: 'Hvorfor enkel' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Sammenlign elselskaber', path: '/elpris-sammenligning' }, { name: `Elselskaber oversigt${place}`, path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={`Elselskaber oversigt 2026${place}`} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Guide · Elselskaber${zoneLabel ? ' · ' + zone : ''} · 2026`}
        h1={`Elselskaber i Danmark 2026${place}: Sådan vælger du rigtigt`}
        intro="En neutral guide til at vælge elselskab. Vi forklarer, hvad der faktisk gør et selskab billigt, hvilke faldgruber du skal undgå, og hvordan du sammenligner objektivt."
        badges={['Spotpris uden tillæg', 'Ingen binding', 'Uafhængige kilder']}
        updated={updated}
        image="https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '3 kriterier', label: 'tillæg · abonnement · services' },
          { value: '0 øre', label: 'enkels tillæg' },
          { value: 'Fra 18 kr./md.', label: 'enkels abonnement' },
          { value: 'Strømligning', label: 'uafhængig kilde' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Sådan vælger du elselskab – kort svar">
        <AnswerBox question="Hvordan finder jeg det rigtige elselskab?">
          Sammenlign på <strong>tre ting</strong>: tillægget til spotprisen (øre/kWh), abonnementet (kr./md.) og de inkluderede services.
          Strømmen er fysisk identisk uanset selskab. Vælg en <strong>gennemsigtig aftale uden tillæg og uden binding</strong> – og medregn
          elbil-fordele som Saving Mode og laderabat, hvis du har elbil. Brug en uafhængig sammenligning som Strømligning.dk for aktuelle tal.
        </AnswerBox>
        {zoneLabel && <p>{geo.name} ligger i <strong>{zoneLabel}</strong>. Spotprisen er ens for alle leverandører i {zone}; det er vilkårene oveni, der adskiller dem.</p>}
      </ProseSection>

      <ProseSection id="kriterier" heading="De 3 kriterier der afgør prisen" alt>
        <FactGrid columns={3} facts={[
          { value: '1. Tillæg', label: 'Øre/kWh oveni spotprisen', sub: 'Lavt = billigt. enkel: 0 øre' },
          { value: '2. Abonnement', label: 'Fast pris pr. måned', sub: 'enkel: fra 18 kr./md.' },
          { value: '3. Services', label: 'Saving Mode, laderabat, service', sub: 'Vigtigt for elbilister' },
        ]} />
        <p className="mt-6">
          Mange fokuserer kun på abonnementet og glemmer tillægget – men ved højt forbrug kan et tillæg på blot 2–8 øre/kWh hurtigt overstige
          besparelsen på et lavt abonnement. Regn altid den samlede pris ud for dit faktiske forbrug.
        </p>
      </ProseSection>

      <ProseSection id="faldgruber" heading="Faldgruber du skal undgå">
        <StepList steps={[
          { title: 'Introtilbud der stiger', body: 'Et lavt tilbud de første måneder kan blive dyrt bagefter. Tjek prisen efter introperioden.' },
          { title: 'Bindingsperiode', body: 'Binding låser dig fast, selv hvis priserne ændrer sig. Vælg aftaler uden binding.' },
          { title: 'Oprettelses- og gebyrer', body: 'Skjulte gebyrer kan æde besparelsen. Gennemsigtige selskaber har ingen.' },
          { title: 'Højt spot-tillæg', body: 'Et tillæg pr. kWh rammer hårdt ved højt forbrug – især med elbil.' },
        ]} />
      </ProseSection>

      <ProseSection id="enkel" heading="Hvorfor enkel energi" alt>
        <p>
          enkel energi er bygget på gennemsigtighed: spotpris uden tillæg, abonnement fra 18 kr./md., ingen binding og ingen skjulte gebyrer.
          For elbilister er Saving Mode, 10 øre/kWh laderabat og serviceaftale inkluderet til 0 kr./md. – og elafgift-refusionen sker automatisk.
        </p>
        <FactGrid columns={3} facts={[
          { value: '0 øre', label: 'Tillæg til spotpris', sub: 'Du betaler markedsprisen 1:1' },
          { value: 'Ingen binding', label: 'Skift når du vil', sub: 'Ingen oprettelsesgebyr' },
          { value: '0 kr./md.', label: 'Saving Mode + service', sub: '+ 10 øre laderabat' },
        ]} />
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elpris-sammenligning', label: 'Sammenlign elselskaber' },
        { path: '/elaftale', label: 'Se el-aftaler' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/hvad-koster-el-gennem-enkel', label: 'Hvad koster el hos enkel?' },
        { path: '/saadan-fungerer-el-markedet', label: 'Sådan fungerer elmarkedet' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
      ]} />

      <RichCTA
        headline="Vælg et gennemsigtigt elselskab"
        subtext="Spotpris uden tillæg fra 18 kr./md., ingen binding, og elbil-fordele inkluderet til 0 kr./md."
        primary={{ path: '/elaftale', text: 'Se el-aftaler' }}
        secondary={{ path: '/elpris-sammenligning', text: 'Sammenlign elselskaber' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}